import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  makeStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {
  ArrowBackIosOutlined,
  ArrowForward,
  ChevronRight,
  Close
} from '@material-ui/icons';
import { colorSet } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { requestEnumToString } from '@utils/enumToString';
import { ServiceTypeArray } from '../serviceTypeData';

interface ServiceTypeInformationDialogProps {
  open: boolean;
  onClose: () => void;
}
const ServiceTypeInformationDialog: React.FC<ServiceTypeInformationDialogProps> = ({
  open,
  onClose
}) => {
  const [selectedServiceType, setSelectedServiceType] = useState<number | null>(
    null
  );
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const { paper, title, content, serviceTypeCard, detailDrawer } = useStyles({
    serviceTypeSelected: selectedServiceType !== null
  });

  return (
    <Dialog classes={{ paper: paper }} open={open} onClose={onClose}>
      {matches && (
        <div className="dialog-mobile-header">
          <IconButton className="close-button" onClick={onClose}>
            <Close />
          </IconButton>
          <CustomizedTypography
            variant="body1"
            fontWeight="500"
            color="textPrimary"
            className="dialog-mobile-header-title"
            noWrap
          >
            제조 서비스별 특징 알아보기
          </CustomizedTypography>
        </div>
      )}
      {!matches && selectedServiceType === null && (
        <DialogTitle className={title} disableTypography>
          <CustomizedTypography
            variant="h3"
            fontWeight="700"
            color="textPrimary"
          >
            제조 서비스별 특징 알아보기
          </CustomizedTypography>
        </DialogTitle>
      )}
      <DialogContent className={content}>
        {(selectedServiceType === null || matches) && (
          <div className="servicetype-list">
            {ServiceTypeArray.map((serviceType, index) => (
              <div
                key={serviceType.id}
                className={serviceTypeCard}
                onClick={(e) => {
                  setSelectedServiceType(index);
                }}
              >
                <CustomizedTypography variant="h4" fontWeight="500">
                  {requestEnumToString(ServiceTypeArray[index].id)}
                </CustomizedTypography>
                <img
                  src={serviceType.imgMobile}
                  width={64}
                  height={64}
                  alt={serviceType.id}
                />
              </div>
            ))}
          </div>
        )}
        {!matches && selectedServiceType !== null && (
          <>
            <div className="detail-header">
              <div className="servicetype-name">
                <IconButton
                  className="back-button"
                  onClick={() => setSelectedServiceType(null)}
                >
                  <ArrowBackIosOutlined />
                </IconButton>
                <CustomizedTypography variant="h3" fontWeight="700">
                  {requestEnumToString(
                    ServiceTypeArray[selectedServiceType].id
                  )}
                </CustomizedTypography>
              </div>
              <IconButton
                className="dialog-close"
                onClick={() => {
                  onClose();
                  setTimeout(() => {
                    setSelectedServiceType(null);
                  }, 300);
                }}
              >
                <Close />
              </IconButton>
            </div>
            <div className="detail-description">
              <CustomizedTypography variant="subtitle1" color="textPrimary">
                {ServiceTypeArray[selectedServiceType].caption}
              </CustomizedTypography>
              <div className="mobile-button-container">
                <CustomizedButton
                  variant={'text'}
                  color={'primary'}
                  endIcon={<ChevronRight />}
                  size="large"
                  fullWidth={false}
                  onClick={(e) => {
                    window.open(ServiceTypeArray[selectedServiceType].knowhow);
                  }}
                >
                  더 알아보기
                </CustomizedButton>
              </div>
            </div>
          </>
        )}
        {matches && (
          <Drawer
            className={detailDrawer}
            anchor="bottom"
            open={selectedServiceType !== null}
            onClose={() => setSelectedServiceType(null)}
          >
            <div className="detail-header">
              <CustomizedTypography variant="h4" fontWeight="700">
                {requestEnumToString(
                  ServiceTypeArray[selectedServiceType as number]?.id
                )}
              </CustomizedTypography>
              <IconButton
                className="back-button"
                onClick={() => setSelectedServiceType(null)}
              >
                <Close />
              </IconButton>
            </div>
            <div className="servicetype-detail">
              <CustomizedTypography variant="subtitle1" color="textPrimary">
                {ServiceTypeArray[selectedServiceType as number]?.caption}
              </CustomizedTypography>
            </div>
            <div className="mobile-button-container">
              <CustomizedButton
                variant={!matches ? 'text' : 'contained'}
                color={!matches ? 'primary' : 'default'}
                endIcon={!matches ? <ArrowForward /> : <></>}
                size="large"
                fullWidth={!matches ? false : true}
                onClick={(e) => {
                  window.open(
                    ServiceTypeArray[selectedServiceType as number]?.knowhow
                  );
                }}
              >
                더 알아보기
              </CustomizedButton>
            </div>
          </Drawer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceTypeInformationDialog;

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    maxWidth: 476,
    height: 600,
    borderRadius: '16px',
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100vw',
      maxHeight: '100%',
      height: '100vh',
      margin: '0',
      borderRadius: '0px',
      '& .dialog-mobile-header': {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${colorSet.gray200}`,
        padding: '0px 16px',
        '& button': {
          transform: 'translateX(-12px)'
        },
        '& .dialog-mobile-header-title': {
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)'
        }
      }
    }
  },

  title: {
    padding: '32px 32px 16px'
  },

  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    '&:first-child': {
      padding: 0
    },
    '& .servicetype-list': {
      paddingTop: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      [theme.breakpoints.down('sm')]: {
        marginBottom: 20
      }
    },
    '& .detail-header': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '4px',
      minHeight: '40px',
      padding: '20px',
      '& .servicetype-name': {
        display: 'flex',
        alignItems: 'center',
        transform: 'translateX(-12px)',
        gap: '4px',
        color: colorSet.gray900,
        '& button': {
          transform: 'translateY(2px)'
        }
      },
      '& .dialog-close': {
        transform: 'translateX(12px)',
        color: colorSet.gray900
      }
    },
    '& .detail-description': {
      padding: '0px 24px',
      '& h6': {
        wordBreak: 'break-all'
      }
    },
    '& .mobile-button-container': {
      marginTop: '24px'
    }
  },

  serviceTypeCard: {
    width: '100%',
    minHeight: '88px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 24px',
    borderRadius: '16px',
    border: `1px solid ${colorSet.gray400}`,
    '& img': {
      borderRadius: '16px'
    },
    '&:hover': {
      border: `2px solid ${colorSet.gray900}`,
      cursor: 'pointer',
      backgroundColor: colorSet.gray100,
      padding: '0px 23px'
    }
  },

  detailDrawer: {
    '& h6': {
      wordBreak: 'break-all'
    },
    '& .MuiDrawer-paper': {
      borderRadius: '16px 16px 0px 0px'
    },
    '& .detail-header': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '20px',
      height: '72px',
      '& .back-button': {
        transform: 'translateX(6px) !important',
        '& svg': {
          color: colorSet.gray900
        }
      }
    },
    '& .servicetype-detail': {
      padding: '0px 20px 32px'
    },
    '& .mobile-button-container': {
      padding: '16px',
      borderTop: `1px solid ${colorSet.gray400}`
    }
  }
}));
